import React, { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import api from "../../chat/api";

/* A finished step of the proposal flow, left in the transcript.
 *
 * The flow's cards all render AFTER the message list and are mutually exclusive
 * on step, so exactly one exists at a time and each replaces the last. Twenty
 * minutes and twelve sections later the conversation held no record that any of
 * it happened - a wizard wearing a chat's clothes.
 *
 * This is the record. One row per completed step, written as the flow advances,
 * so scrolling back shows what was approved and what was rewritten.
 *
 * QUIET BY DEFAULT. These stack — twelve of them by the end of a run — so the
 * row is a plain bordered line in the ordinary text colour, not a coloured
 * announcement. A green heading per section turned the transcript into a wall
 * of green and made every step shout equally loudly; the state now lives in a
 * small tinted glyph, which is enough to scan by and quiet enough to ignore.
 *
 * AND IT OPENS. "1. Executive Summary drafted" is a claim about work you cannot
 * see, and the section it refers to has long scrolled out of the panel. Opening
 * the row shows the prose that was actually written.
 */

const TONE = {
  done: {
    ink: "text-[#1F7D38]",
    icon: (
      <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  redo: {
    ink: "text-[#92620B]",
    icon: (
      <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <polyline points="3 3 3 8 8 8" />
      </svg>
    ),
  },
  step: {
    ink: "text-[#8E4187]",
    icon: (
      <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="13 2 4 14 12 14 11 22 20 10 12 10 13 2" />
      </svg>
    ),
  },
  /* STILL HAPPENING. A finished row is a claim that something completed, and
   * showing one while the work is still running is how a minute of retrieval
   * reads as a hang: three static rows, nothing moving, no way to tell a live
   * request from a dead one. This tone is posted when a phase STARTS and patched
   * to `step` when it finishes, so there is always exactly one row in motion. */
  running: {
    ink: "text-[#8E4187]",
    spin: true,
    icon: (
      <span className="block h-[11px] w-[11px] animate-spin rounded-full border-[1.6px] border-current border-t-transparent motion-reduce:animate-none" />
    ),
  },
};

/* Matches the document panel's measure and rhythm, so the same paragraph reads
 * the same in both places. `.sensa-doc` carries the heading and list treatment. */
const PROSE = "sensa-doc max-w-[34rem] text-[13px] leading-[1.75] text-[#42404A]";

/* Sized to the cards it sits between.
 *
 * The flow's cards are max-w-[640px] (CopilotCardShell) and these rows ran the
 * full width of the column, so the record and the thing it records had two
 * different right edges and the run read as two stacked layouts.
 *
 * Border at 0.14 rather than 0.08: at eight percent on white the box was there
 * in the markup and absent on screen. */
const ROW =
  "flex w-full max-w-[640px] items-center gap-2.5 rounded-lg border border-[#18101C]/[0.14] " +
  "bg-[#18101C]/[0.015] px-3 py-2 text-left transition-colors duration-150 motion-reduce:transition-none";

/* Drafted content, however the pipeline stored it.
 *
 * Sections come back as PLAIN TEXT far more often than as markup, and pushing
 * text through dangerouslySetInnerHTML collapses every newline — a six
 * paragraph section arrives as one slab. Markup is rendered as markup (and
 * sanitised, since it is model-written); text is split on blank lines into real
 * paragraphs. */
const Prose = ({ text }) => {
  const stored = String(text || "");
  if (!stored.trim()) return null;
  if (/<[a-z][\s\S]*?>/i.test(stored)) {
    return (
      <div
        className={PROSE}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stored) }}
      />
    );
  }
  return (
    <div className={PROSE}>
      {stored
        .split(/\n\s*\n/)
        .map((para) => para.trim())
        .filter(Boolean)
        .map((para, i) => (
          <p key={i} className={i ? "mt-[0.85em]" : ""}>
            {para.replace(/\s*\n\s*/g, " ")}
          </p>
        ))}
    </div>
  );
};

const ProposalStepRow = ({ message, onOpenSection }) => {
  const { label, detail, tone = "step", projectId, sectionId } = message || {};
  const t = TONE[tone] || TONE.step;

  const [open, setOpen] = useState(false);
  const [nodes, setNodes] = useState(null);
  const [failed, setFailed] = useState(false);

  const canRead = !!projectId && !!sectionId;

  /* Fetched when opened, never before.
   *
   * A run leaves a dozen of these rows behind and almost none are ever opened;
   * loading the document for all twelve on restore would be a request per
   * section to render lines nobody asked to expand. Fetched fresh rather than
   * stored, too — the section may have been rewritten since, and a record
   * showing the superseded draft is worse than one showing none.
   */
  const load = useCallback(async () => {
    if (!canRead || nodes) return;
    try {
      const res = await api.get(`/api/v1/rfp/folders/${projectId}/section-view/`);
      const all = Array.isArray(res?.data?.sections) ? res.data.sections : [];
      const found = all.find((s) => String(s.id) === String(sectionId));
      if (!found) {
        setFailed(true);
        return;
      }
      // Intro first, then each subsection: the order the document reads in.
      setNodes(
        [{ id: found.id, title: null, response: found.response }]
          .concat((found.children || []).map((c) => ({ id: c.id, title: c.title, response: c.response })))
          .filter((n) => String(n.response || "").trim())
      );
    } catch {
      setFailed(true);
    }
  }, [canRead, nodes, projectId, sectionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const body = (
    <>
      <span className={`flex-none ${t.ink}`}>{t.icon}</span>
      <span
        className={`min-w-0 truncate text-[12.5px] font-medium text-[#3F3F46] ${
          t.spin ? "animate-pulse motion-reduce:animate-none" : ""
        }`}
      >
        {label}
        {/* An ellipsis only while it runs, so the label reads as an action in
            progress rather than a completed one. */}
        {t.spin && <span className="text-[#9A93A1]">…</span>}
      </span>
      {detail && (
        <span className="min-w-0 flex-none truncate text-[12px] tabular-nums text-[#9A93A1]">
          {detail}
        </span>
      )}
    </>
  );

  return (
    <div className="pl-10 animate-copilot-rise motion-reduce:animate-none">
      {canRead ? (
        /* The whole row is the control. A separate button beside a label that
         * plainly refers to something you cannot see is two things where one
         * will do. */
        <button
          type="button"
          onClick={() => {
            const next = !open;
            setOpen(next);
            // Opening a record also takes the document panel to that section:
            // the row answers "what did it write", the panel answers "how does
            // it read in place", and wanting one usually means wanting both.
            if (next) onOpenSection?.(sectionId);
          }}
          aria-expanded={open}
          className={`${ROW} hover:border-[#18101C]/[0.22] hover:bg-[#18101C]/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]`}
        >
          {body}
          <span className="ml-auto flex-none text-[#9A93A1]">
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-200 motion-reduce:transition-none ${
                open ? "rotate-180" : ""
              }`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      ) : (
        <div className={ROW}>{body}</div>
      )}

      {open && canRead && (
        <div className="mt-1 max-h-[24rem] w-full max-w-[640px] overflow-y-auto rounded-lg border border-[#18101C]/[0.14] bg-white px-3.5 py-3">
          {failed ? (
            <p className="text-[12px] text-[#77707E]">
              Couldn&apos;t load this section — it may have been removed.
            </p>
          ) : !nodes ? (
            <p className="text-[12px] text-[#77707E]">Loading…</p>
          ) : nodes.length === 0 ? (
            <p className="text-[12px] text-[#77707E]">Nothing was written for this section.</p>
          ) : (
            nodes.map((n) => (
              <div key={n.id} className="mt-4 first:mt-0">
                {n.title && (
                  <p className="mb-1 text-[12.5px] font-semibold text-[#18101C]">{n.title}</p>
                )}
                <Prose text={n.response} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProposalStepRow;
